// frontend/src/components/ui/SegmentedControl.tsx
// Segmented filter primitive matching proto `.seg` / `button.on`.
// Presentational only — no data fetching, no mutations.

import { cn } from "../../lib/cn";

export interface SegOption<T extends string = string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string = string> {
  options: SegOption<T>[];
  value: T;
  onChange: (v: T) => void;
  /** ARIA group label — also rendered visually if provided */
  label?: string;
  className?: string;
}

export function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  label,
  className,
}: SegmentedControlProps<T>): JSX.Element {
  return (
    <div role="group" aria-label={label}>
      {/* Proto .seg container */}
      <div
        className={cn(
          "inline-flex bg-surface-2 border border-border rounded-[9px] p-[3px] gap-[2px] flex-wrap",
          className,
        )}
      >
        {options.map((opt) => {
          const isActive = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              data-active={isActive || undefined}
              aria-pressed={isActive}
              className={cn(
                // Proto .seg button base
                "h-[32px] px-[11px] text-[12px] rounded-[6px] transition-[background,box-shadow,color]",
                "border-0 font-medium whitespace-nowrap",
                "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/20",
                // Mobile touch target
                "min-h-[44px] sm:min-h-[32px]",
                isActive
                  ? // Proto button.on
                    "bg-surface text-accent font-semibold shadow-card"
                  : "bg-transparent text-muted hover:text-text",
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
